(() => {
  'use strict';

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const allowedTargets = [3, 6, 12];
  document.documentElement.classList.add('bte-js');

  class BasicTexEssentialsCombo {
    constructor(root) {
      this.root = root;
      this.config = this.readJSON(root.querySelector('[data-bte-config]')) || {};
      this.storageKey = this.config.storageKey || 'basictex:esenciales-combo:v1';
      this.state = this.load();
      this.drawer = root.querySelector('[data-bte-drawer]');
      this.overlay = root.querySelector('[data-bte-overlay]');
      this.error = root.querySelector('[data-bte-error]');
      this.announcer = root.querySelector('[data-bte-announcer]');
      this.lastFocus = null;
      this.isOpen = false;
      this.bind();
      this.render();
      this.revealMedia(document);
      this.initVideos(document);
      document.dispatchEvent(new CustomEvent('basictex:combo-ready', { detail: this.snapshot() }));
    }

    readJSON(node) {
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (error) { return null; }
    }

    load() {
      const empty = { target: 0, items: [], updatedAt: Date.now() };
      try {
        const parsed = JSON.parse(window.localStorage.getItem(this.storageKey) || 'null');
        if (!parsed || !allowedTargets.includes(Number(parsed.target)) || !Array.isArray(parsed.items)) return empty;
        return {
          target: Number(parsed.target),
          items: parsed.items.filter((item) => item && item.variantId && item.productId).slice(0, Number(parsed.target)),
          updatedAt: Number(parsed.updatedAt || Date.now())
        };
      } catch (error) {
        return empty;
      }
    }

    save(message = '') {
      this.state.updatedAt = Date.now();
      try { window.localStorage.setItem(this.storageKey, JSON.stringify(this.state)); } catch (error) {}
      this.render();
      const detail = this.snapshot();
      document.dispatchEvent(new CustomEvent('basictex:combo-change', { detail }));
      if (message) this.announce(message);
    }

    snapshot() {
      return {
        target: Number(this.state.target || 0),
        items: this.state.items.map((item) => ({ ...item })),
        count: this.state.items.length,
        complete: Boolean(this.state.target && this.state.items.length === this.state.target)
      };
    }

    bind() {
      this.root.addEventListener('click', (event) => {
        const launch = event.target.closest('[data-bte-launch]');
        if (launch) {
          this.open(this.state.target ? 'review' : 'tiers', launch);
          return;
        }
        if (event.target.closest('[data-bte-close]') || event.target === this.overlay) {
          this.close();
          return;
        }
        if (event.target.closest('[data-bte-review]')) {
          this.open('review', event.target.closest('button'));
          return;
        }
        const tier = event.target.closest('[data-bte-select-target]');
        if (tier) {
          if (this.setTarget(Number(tier.dataset.target))) this.showView('review');
          return;
        }
        const remove = event.target.closest('[data-bte-remove]');
        if (remove) {
          this.removeItem(Number(remove.dataset.bteRemove));
          return;
        }
        if (event.target.closest('[data-bte-change-target]')) {
          this.showView('tiers');
          return;
        }
        if (event.target.closest('[data-bte-finish]')) {
          this.finish();
        }
      });

      document.addEventListener('click', (event) => {
        const productTier = event.target.closest('[data-bte-product-tier]');
        if (productTier) {
          const wrapper = productTier.closest('[data-bte-product-combo]');
          const changed = this.setTarget(Number(productTier.dataset.target), { quiet: true });
          if (!changed) {
            this.renderTierStates(Number(this.state.target || 0));
            const status = wrapper?.querySelector('[data-bte-product-status]');
            if (status) status.textContent = `Tu combo ya tiene ${this.state.items.length} camisetas. Elimina algunas antes de reducirlo.`;
          }
          return;
        }

        const addCurrent = event.target.closest('[data-bte-add-current]');
        if (addCurrent) {
          this.addCurrentProduct(addCurrent);
          return;
        }

        const legacyTier = event.target.closest('[data-btc-tier]');
        if (legacyTier && allowedTargets.includes(Number(legacyTier.dataset.btcQuantity))) {
          this.setTarget(Number(legacyTier.dataset.btcQuantity), { quiet: true });
          return;
        }

        const start = event.target.closest('[data-bte-start-target]');
        if (start) {
          event.preventDefault();
          const changed = this.setTarget(Number(start.dataset.target));
          if (changed && start.dataset.redirect) {
            window.setTimeout(() => window.location.assign(start.dataset.redirect), reduceMotion.matches ? 0 : 180);
          } else if (changed && start.href) {
            window.location.assign(start.href);
          }
        }
      });

      document.addEventListener('keydown', (event) => {
        if (!this.isOpen) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          this.close();
          return;
        }
        if (event.key === 'Tab') this.trapFocus(event);
      });

      document.addEventListener('shopify:section:load', (event) => {
        this.revealMedia(event.target);
        this.initVideos(event.target);
      });
    }

    setTarget(target, options = {}) {
      if (!allowedTargets.includes(target)) return false;
      if (this.state.items.length > target) {
        this.setError(`Tienes ${this.state.items.length} camisetas seleccionadas. Elimina algunas antes de cambiar al combo de ${target}.`);
        if (!options.quiet) this.open('review');
        return false;
      }
      this.state.target = target;
      this.setError('');
      this.save(options.quiet ? '' : `Combo de ${target} seleccionado. Empieza a elegir tus camisetas.`);
      return true;
    }

    getSelectedItem(button) {
      const wrapper = button.closest('[data-bte-product-combo]');
      const data = this.readJSON(wrapper && wrapper.querySelector('[data-bte-product-json]'));
      if (!wrapper || !data) return { error: 'No pudimos leer la información de este producto.' };
      const info = wrapper.closest('.t4s-product__info-container') || document;
      const variantField = info.querySelector('form[data-type="add-to-cart-form"] [name="id"], form[action*="/cart/add"] [name="id"]');
      const variantId = String(variantField ? variantField.value : (data.variants[0] && data.variants[0].id) || '');
      const variant = data.variants.find((item) => String(item.id) === variantId);
      if (!variant) return { error: 'Selecciona primero un color y una talla.' };
      if (!variant.available) return { error: 'Esta combinación está agotada. Elige otra talla o color.' };
      return {
        item: {
          variantId: String(variant.id),
          productId: String(data.productId),
          handle: data.handle || '',
          title: data.title || '',
          variantTitle: this.translateVariant(variant.title || variant.options.join(' / ')),
          image: variant.image || '',
          url: data.url || ''
        },
        wrapper
      };
    }

    addCurrentProduct(button) {
      const selected = this.getSelectedItem(button);
      const wrapper = selected.wrapper || button.closest('[data-bte-product-combo]');
      const status = wrapper && wrapper.querySelector('[data-bte-product-status]');
      if (selected.error) {
        if (status) status.textContent = selected.error;
        return;
      }
      const preferred = Number(wrapper.querySelector('[data-bte-product-tier].is-selected')?.dataset.target || 3);
      if (!this.state.target && !this.setTarget(preferred, { quiet: true })) return;
      if (this.state.target !== preferred && this.state.items.length === 0) this.setTarget(preferred, { quiet: true });
      if (this.state.items.length >= this.state.target) {
        if (status) status.textContent = 'Tu combo ya está completo. Revísalo para continuar.';
        this.open('review', button);
        return;
      }
      this.state.items.push(selected.item);
      const count = this.state.items.length;
      const remaining = this.state.target - count;
      const message = remaining > 0
        ? `${selected.item.title} agregada. Te ${remaining === 1 ? 'falta' : 'faltan'} ${remaining} para completar el combo.`
        : `Combo de ${this.state.target} completo. Revisa tus camisetas.`;
      if (status) status.textContent = message;
      this.save(message);
      if (remaining === 0) {
        this.root.classList.add('is-celebrating');
        window.setTimeout(() => this.root.classList.remove('is-celebrating'), 900);
        window.setTimeout(() => this.open('review', button), reduceMotion.matches ? 0 : 320);
      }
    }

    removeItem(index) {
      if (!Number.isInteger(index) || index < 0 || index >= this.state.items.length) return;
      const removed = this.state.items.splice(index, 1)[0];
      this.save(`${removed.title} fue eliminada del combo.`);
    }

    render() {
      const target = Number(this.state.target || 0);
      const count = this.state.items.length;
      const complete = Boolean(target && count === target);
      document.body.classList.toggle('bte-combo-active', Boolean(target));
      const progress = this.root.querySelector('[data-bte-progress]');
      if (progress) {
        progress.hidden = !target;
        progress.style.setProperty('--bte-progress', target ? String(Math.min(count / target, 1)) : '0');
      }
      this.root.querySelectorAll('[data-bte-count], [data-bte-review-count]').forEach((node) => { node.textContent = String(count); });
      this.root.querySelectorAll('[data-bte-target], [data-bte-review-target]').forEach((node) => { node.textContent = String(target || 3); });
      const progressText = this.root.querySelector('[data-bte-progress-text]');
      const reviewMessage = this.root.querySelector('[data-bte-review-message]');
      if (target) {
        const remaining = Math.max(target - count, 0);
        const text = complete
          ? 'Combo completo · revísalo'
          : count === 0
            ? `Elige ${target} camisetas`
            : `Te ${remaining === 1 ? 'falta' : 'faltan'} ${remaining} para desbloquearlo`;
        if (progressText) progressText.textContent = text;
        if (reviewMessage) reviewMessage.textContent = complete ? 'Tu precio especial está listo' : text;
      }
      const reviewFill = this.root.querySelector('[data-bte-review-fill]');
      if (reviewFill) reviewFill.style.transform = `scaleX(${target ? Math.min(count / target, 1) : 0})`;

      this.renderThumbs();
      this.renderItems();
      this.renderTierStates(target);
      const finish = this.root.querySelector('[data-bte-finish]');
      if (finish) {
        finish.disabled = !complete;
        finish.querySelector('span').textContent = complete ? `AGREGAR COMBO DE ${target} AL CARRITO` : `FALTAN ${Math.max(target - count, 0)} CAMISETAS`;
      }
    }

    renderTierStates(target) {
      document.querySelectorAll('[data-bte-select-target], [data-bte-product-tier]').forEach((node) => {
        const selected = Number(node.dataset.target) === target;
        node.classList.toggle('is-selected', selected);
        node.setAttribute('aria-checked', String(selected));
      });
    }

    renderThumbs() {
      const container = this.root.querySelector('[data-bte-progress-thumbs]');
      if (!container) return;
      container.replaceChildren(...this.state.items.slice(-6).map((item) => {
        const frame = document.createElement('span');
        if (item.image) {
          const image = document.createElement('img');
          image.src = item.image;
          image.alt = '';
          image.loading = 'lazy';
          frame.appendChild(image);
        }
        return frame;
      }));
    }

    renderItems() {
      const container = this.root.querySelector('[data-bte-items]');
      if (!container) return;
      if (!this.state.items.length) {
        container.innerHTML = '<div class="bte-combo-empty">Aún no has agregado camisetas. Elige un diseño, su color y su talla.</div>';
        return;
      }
      container.innerHTML = this.state.items.map((item, index) => `
        <article class="bte-combo-item">
          ${item.image ? `<img src="${this.escape(item.image)}" alt="" loading="lazy" width="64" height="64">` : '<span class="bte-combo-item__placeholder" aria-hidden="true"></span>'}
          <div class="bte-combo-item__copy">
            <strong>${this.escape(item.title)}</strong>
            <small>${this.escape(item.variantTitle || 'Variante seleccionada')}</small>
          </div>
          <button class="bte-combo-item__remove" type="button" data-bte-remove="${index}" aria-label="Eliminar ${this.escape(item.title)} del combo">
            <svg aria-hidden="true" viewBox="0 0 24 24"><path d="M4 7h16M9 7V4h6v3m-9 0 1 13h10l1-13M10 11v5m4-5v5"/></svg>
          </button>
        </article>`).join('');
    }

    showView(view) {
      const tierView = this.root.querySelector('[data-bte-tier-view]');
      const reviewView = this.root.querySelector('[data-bte-review-view]');
      if (tierView) tierView.hidden = view !== 'tiers';
      if (reviewView) reviewView.hidden = view !== 'review';
      this.setError('');
    }

    open(view = 'tiers', trigger = null) {
      if (!this.drawer || !this.overlay) return;
      this.lastFocus = trigger || document.activeElement;
      this.showView(view);
      this.drawer.hidden = false;
      this.overlay.hidden = false;
      document.body.classList.add('bte-combo-lock');
      this.isOpen = true;
      window.requestAnimationFrame(() => {
        this.drawer.classList.add('is-open');
        this.overlay.classList.add('is-open');
        this.drawer.querySelector('[data-bte-close]')?.focus({ preventScroll: true });
      });
    }

    close() {
      if (!this.isOpen) return;
      this.drawer.classList.remove('is-open');
      this.overlay.classList.remove('is-open');
      document.body.classList.remove('bte-combo-lock');
      this.isOpen = false;
      const finish = () => {
        if (!this.isOpen) {
          this.drawer.hidden = true;
          this.overlay.hidden = true;
        }
      };
      window.setTimeout(finish, reduceMotion.matches ? 0 : 330);
      if (this.lastFocus && typeof this.lastFocus.focus === 'function') this.lastFocus.focus({ preventScroll: true });
    }

    trapFocus(event) {
      const focusable = Array.from(this.drawer.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')).filter((node) => !node.hidden && node.offsetParent !== null);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    async finish() {
      if (!this.state.target || this.state.items.length !== this.state.target) return;
      const button = this.root.querySelector('[data-bte-finish]');
      const original = button.querySelector('span').textContent;
      button.disabled = true;
      button.classList.add('is-loading');
      button.querySelector('span').textContent = 'PREPARANDO TU COMBO…';
      this.setError('');
      const grouped = new Map();
      this.state.items.forEach((item) => {
        const key = String(item.variantId);
        const existing = grouped.get(key);
        grouped.set(key, {
          id: item.variantId,
          quantity: (existing ? existing.quantity : 0) + 1,
          properties: { '_Combo BasicTex': `Esenciales x${this.state.target}` }
        });
      });
      try {
        const response = await fetch(this.config.cartAddUrl || '/cart/add.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: Array.from(grouped.values()) })
        });
        if (!response.ok) {
          const payload = await response.json().catch(() => ({}));
          throw new Error(payload.description || 'No pudimos agregar todas las camisetas. Revisa la disponibilidad.');
        }
        const target = this.state.target;
        const code = this.config.tiers && this.config.tiers[String(target)] ? this.config.tiers[String(target)].code : '';
        this.state = { target: 0, items: [], updatedAt: Date.now() };
        try { window.localStorage.removeItem(this.storageKey); } catch (error) {}
        if (code) {
          const rootPath = window.Shopify?.routes?.root || '/';
          window.location.assign(`${rootPath}discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(this.config.cartUrl || '/cart')}`);
        } else {
          window.location.assign(this.config.cartUrl || '/cart');
        }
      } catch (error) {
        this.setError(error.message || 'Ocurrió un error. Intenta nuevamente.');
        button.disabled = false;
        button.classList.remove('is-loading');
        button.querySelector('span').textContent = original;
      }
    }

    translateVariant(value) {
      const map = {
        black: 'Negro', white: 'Blanco', beige: 'Beige', cream: 'Crema', gray: 'Gris', grey: 'Gris',
        red: 'Rojo', blue: 'Azul', green: 'Verde', yellow: 'Amarillo', pink: 'Rosado', brown: 'Café'
      };
      return String(value || '').split(' / ').map((part) => map[part.trim().toLowerCase()] || part).join(' / ');
    }

    setError(message) {
      if (this.error) this.error.textContent = message || '';
    }

    announce(message) {
      if (!this.announcer) return;
      this.announcer.textContent = '';
      window.setTimeout(() => { this.announcer.textContent = message; }, 20);
    }

    escape(value) {
      return String(value || '').replace(/[&<>'"]/g, (character) => ({
        '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
      })[character]);
    }

    revealMedia(root = document) {
      const elements = root.querySelectorAll('[data-bte-reveal]');
      if (!elements.length) return;
      if (reduceMotion.matches || !('IntersectionObserver' in window)) {
        elements.forEach((node) => node.classList.add('is-visible'));
        return;
      }
      const observer = new IntersectionObserver((entries, instance) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          instance.unobserve(entry.target);
        });
      }, { threshold: .08, rootMargin: '0px 0px -5% 0px' });
      elements.forEach((node) => observer.observe(node));
    }

    initVideos(root = document) {
      root.querySelectorAll('[data-bte-video]').forEach((wrapper) => {
        if (wrapper.dataset.bteReady) return;
        const video = wrapper.querySelector('video');
        const toggle = wrapper.querySelector('[data-bte-video-toggle]');
        if (!video) return;
        wrapper.dataset.bteReady = 'true';
        video.muted = true;
        video.playsInline = true;
        const sync = () => {
          if (!toggle) return;
          toggle.classList.toggle('is-paused', video.paused);
          toggle.setAttribute('aria-label', video.paused ? 'Reproducir video' : 'Pausar video');
        };
        if (toggle) toggle.addEventListener('click', () => {
          if (video.paused) video.play().catch(() => {}); else video.pause();
          window.setTimeout(sync, 20);
        });
        if (reduceMotion.matches) {
          video.pause();
          sync();
        } else if ('IntersectionObserver' in window) {
          new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting && entry.intersectionRatio > .3) video.play().catch(() => {});
            else video.pause();
            sync();
          }, { threshold: [0,.3,.7] }).observe(wrapper);
        }
      });
    }
  }

  const init = (root = document) => {
    const section = root.querySelector('[data-bte-system]') || (root.matches?.('[data-bte-system]') ? root : null);
    if (!section || section.dataset.bteInitialized) return;
    section.dataset.bteInitialized = 'true';
    window.BasicTexEssentialsCombo = new BasicTexEssentialsCombo(section);
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
